/* eslint-disable */
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

// for local storage
const STORAGE_KEYS = {
  PROFILES: "foorweb_saved_profiles",
  CURRENT_PROFILE: "foorweb_current_profile_uid",
  CREDENTIALS: "foorweb_cred_", // + uid
};

class ProfileManager {
  static getSavedProfiles() {
    try {
      const json = localStorage.getItem(STORAGE_KEYS.PROFILES);
      if (!json) return [];
      const profiles = JSON.parse(json);
      // sorted by lastUsed
      return profiles.sort(
        (a, b) => new Date(b.lastUsed) - new Date(a.lastUsed)
      );
    } catch (err) {
      console.error("Error getting saved profiles:", err);
      return [];
    }
  }

  // save or update a profile and set it as current
  static async saveProfile({ uid, email, name, photoUrl }) {
    try {
      const profiles = this.getSavedProfiles();
      const index = profiles.findIndex((p) => p.uid === uid);

      const newProfile = {
        uid,
        email,
        name,
        photoUrl: photoUrl || null,
        lastUsed: new Date().toISOString(),
      };

      if (index >= 0) {
        profiles[index] = newProfile;
      } else {
        profiles.push(newProfile);
      }

      localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles));
      this.setCurrentProfile(uid);

      return newProfile;
    } catch (err) {
      console.error("Error saving profile:", err);
      throw err;
    }
  }

  // Remove profile completely
  static async removeProfile(uid) {
    try {
      const profiles = this.getSavedProfiles().filter((p) => p.uid !== uid);
      localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles));

      // Remove encrypted credentials
      localStorage.removeItem(STORAGE_KEYS.CREDENTIALS + uid);

      // If current profile, clear it
      if (this.getCurrentProfileId() === uid) {
        localStorage.removeItem(STORAGE_KEYS.CURRENT_PROFILE);
      }

      //clear user-specific cache
      this.clearUserCache(uid);
    } catch (err) {
      console.error("Error removing profile:", err);
      throw err;
    }
  }

  static getCurrentProfileId() {
    return localStorage.getItem(STORAGE_KEYS.CURRENT_PROFILE);
  }

  static setCurrentProfile(uid) {
    localStorage.setItem(STORAGE_KEYS.CURRENT_PROFILE, uid);

    const profiles = this.getSavedProfiles();
    const index = profiles.findIndex((p) => p.uid === uid);
    if (index >= 0) {
      profiles[index].lastUsed = new Date().toISOString();
      localStorage.setItem(STORAGE_KEYS.PROFILES, JSON.stringify(profiles));
    }
  }

  static getProfile(uid) {
    const profiles = this.getSavedProfiles();
    return profiles.find((p) => p.uid === uid) || null;
  }

  static getCurrentProfile() {
    const uid = this.getCurrentProfileId();
    if (!uid) return null;
    return this.getProfile(uid);
  }

  static clearCurrentProfile() {
    const uid = this.getCurrentProfileId();
    if (uid) {
      // remove current profile pointer
      localStorage.removeItem(STORAGE_KEYS.CURRENT_PROFILE);

      // clear cache of this profile
      this.clearUserCache(uid);
    }
  }

  static hasMultipleProfiles() {
    return this.getSavedProfiles().length > 1;
  }

  // encrypt and store credentials securely
  static storeCredentials({ uid, email, password }) {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify({ email, password }),
        ENCRYPTION_KEY
      ).toString();
      localStorage.setItem(STORAGE_KEYS.CREDENTIALS + uid, encrypted);
      return true;
    } catch (err) {
      console.error("Error storing credentials:", err);
      return false;
    }
  }

  // Retrieve and decrypt credentials
  static getCredentials(uid) {
    try {
      const encrypted = localStorage.getItem(STORAGE_KEYS.CREDENTIALS + uid);
      if (!encrypted) return null;

      const bytes = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
      const creds = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      return creds;
    } catch (err) {
      console.error("Error getting credentials:", err);
      return null;
    }
  }

  // clear cache specific to a UID
  static clearUserCache(uid) {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        key.includes(uid) &&
        !key.startsWith(STORAGE_KEYS.CREDENTIALS)
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  }

  // clear all profiles and credentials
  static clearAllProfiles() {
    const profiles = this.getSavedProfiles();
    profiles.forEach((p) =>
      localStorage.removeItem(STORAGE_KEYS.CREDENTIALS + p.uid)
    );
    localStorage.removeItem(STORAGE_KEYS.PROFILES);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_PROFILE);
  }

  static async switchProfile(uid, onSuccess, onError) {
    try {
      const auth = getAuth();
      await signOut(auth);

      const creds = this.getCredentials(uid);
      if (!creds) throw new Error("Credentials not found. Please login again.");

      const userCredential = await signInWithEmailAndPassword(
        auth,
        creds.email,
        creds.password
      );
      const user = userCredential.user;
      const token = await user.getIdToken();

      // set current profile
      this.setCurrentProfile(uid);

      // update tokens in localStorage
      localStorage.setItem("token", user.uid);
      localStorage.setItem("auth-token", token);

      // clear cache of previous user
      this.clearUserCache(uid);

      if (onSuccess) onSuccess(user);
      return user;
    } catch (err) {
      console.error("Error switching profile:", err);
      if (onError) onError(err);
      throw err;
    }
  }
}

export default ProfileManager;
